import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import DynamicLoader from './components/DynamicLoader';
import ProtectedRoute from './components/ProtectedRoute';
import NexusMobileFrame from './layout/NexusMobileFrame';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard_Final';
import Studios from './pages/Studios';
import Queue from './pages/Queue';
import QueueReview from './pages/QueueReview';
import MySubmissions from './pages/MySubmissions';
import ProductionMode from './pages/ProductionMode';
import AnalyticsDashboard_v2 from './pages/AnalyticsDashboard_v2';
import Announcements from './pages/Announcements';
import CommunicationsHub from './pages/CommunicationsHub';
import WhatsAppSettings from './pages/WhatsAppSettings';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import FunctionalGroupsPage from './pages/admin/FunctionalGroupsPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import IntegrationsPage from './pages/admin/IntegrationsPage';
import AIModelPage from './pages/admin/AIModelPage';
import BackupPage from './pages/admin/BackupPage';
import WhatsAppTemplatesPage from './pages/admin/WhatsAppTemplatesPage';
import PurposeTagsPage from './pages/admin/PurposeTagsPage';
import AIUsagePage from './pages/AIUsagePage';
import SOASettingsPage from './pages/SOASettingsPage';
import SSOSettingsPage from './pages/admin/SSOSettingsPage';
import SSOCallback from './pages/SSOCallback';
import SSONexusLaunch from './pages/SSONexusLaunch';
import OrgAdoptionPage from './pages/admin/OrgAdoptionPage';
import OrgAdoptionInactivePage from './pages/admin/OrgAdoptionInactivePage';
import ContentStudio from './pages/ContentStudio';
import ContentStudioV2TestHarness from './pages/ContentStudioV2TestHarness';
import ContentStudioV2 from './pages/ContentStudioV2';
import LiveStudio from './pages/LiveStudio';
import LiveStudioV2 from './pages/liveV2/LiveStudioV2';
import Teleprompter from './pages/liveV2/Teleprompter';
import LiveMonitor from './pages/LiveMonitor';
import CopywritingStudio from './pages/CopywritingStudio';
import AnalystStudio from './pages/AnalystStudio';
import SOADrill from './pages/SOADrill';
import KnowledgePackManager from './pages/KnowledgePackManager';
import KnowledgePackManagerV3 from './pages/KnowledgePackManagerV3';
import KnowledgePackWizard from './pages/KnowledgePackWizard';
import KnowledgePackWizardV3 from './pages/KnowledgePackWizardV3';
import KnowledgePackWizardCompact from './pages/KnowledgePackWizardCompact';
import KnowledgePackVersionHistory from './pages/KnowledgePackVersionHistory';
import KnowledgePackAuditLog from './pages/KnowledgePackAuditLog';
import CampaignGraphView from './pages/CampaignGraphView';
import CampaignDirectionManager from './pages/CampaignDirectionManager';
import ProductPackEditorV4 from './components/ProductPackEditorV4';
import AmbassadorPackEditorV4 from './components/AmbassadorPackEditorV4';
import SpiderWeb from './pages/SpiderWeb';
import SpiderWebV3 from './pages/SpiderWebV3';
import ChangePasswordPage from './pages/ChangePasswordPage';
import CampaignDashboard from './pages/CampaignDashboard';
import CampaignWizardCompact from './pages/CampaignWizardCompact';
import PerformanceAnalyticsDashboard from './pages/PerformanceAnalyticsDashboard';
import UnifiedAnalyticsHub from './pages/UnifiedAnalyticsHub';
import Tutorial from './pages/Tutorial';
import './App.css';

// Version tracking for cache verification
const APP_VERSION = '6.3.3-COMPACT-WIZARD-UNIFIED';
const BUILD_TIMESTAMP = new Date().toISOString();
const CACHE_BUSTER = Date.now(); // Force cache invalidation
console.log(`[EMZI NEXUS] Version: ${APP_VERSION} | Build: ${BUILD_TIMESTAMP} | Cache: ${CACHE_BUSTER}`);

// Mobile frame layout wrapper with global progress loader
const AppLayout = ({ children }) => {
  const { activeTaskId } = useLoading();
  
  return (
    <>
      <NexusMobileFrame>{children}</NexusMobileFrame>
      {/* Global progress loader - shows for any active task */}
      {activeTaskId && (
        <DynamicLoader
          taskId={activeTaskId}
          variant="modal"
          onRetry={() => {
            // Retry is handled per-component
            console.log('[AppLayout] Retry requested for task:', activeTaskId);
          }}
        />
      )}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <LoadingProvider>
          <BrowserRouter>
            <NavigationProvider>
              <AppLayout>
            <Routes>
              <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />

            <Route path="/studios" element={
              <ProtectedRoute>
                <Studios />
              </ProtectedRoute>
            } />

            <Route path="/queue" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'admin']}>
                <Queue />
              </ProtectedRoute>
            } />

            <Route path="/queue/review/:sessionId" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'admin']}>
                <QueueReview />
              </ProtectedRoute>
            } />

            <Route path="/my-submissions" element={
              <ProtectedRoute>
                <MySubmissions />
              </ProtectedRoute>
            } />

            <Route path="/production-mode" element={
              <ProtectedRoute>
                <ProductionMode />
              </ProtectedRoute>
            } />

            {/* Unified Analytics Hub (System + Knowledge Packs + Studios) */}
            <Route path="/analytics" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <UnifiedAnalyticsHub />
              </ProtectedRoute>
            } />

            {/* Legacy redirects for backward compatibility */}
            <Route path="/performance-analytics" element={
              <Navigate to="/analytics?tab=knowledge-packs" replace />
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />

            <Route path="/communications" element={
              <ProtectedRoute allowedRoles={['management_functional', 'management_global', 'admin', 'supervisor', 'executor']}>
                <CommunicationsHub />
              </ProtectedRoute>
            } />

            <Route path="/announcements" element={
              <ProtectedRoute allowedRoles={['management_functional', 'management_global', 'admin']}>
                <Announcements />
              </ProtectedRoute>
            } />
            
            {/* Legacy WhatsApp routes - Redirect to Communications Hub */}
            <Route path="/whatsapp-settings" element={
              <ProtectedRoute allowedRoles={['management_functional', 'management_global', 'admin']}>
                <Navigate to="/communications?tab=accounts" replace />
              </ProtectedRoute>
            } />
            <Route path="/whatsapp" element={
              <ProtectedRoute allowedRoles={['management_functional', 'management_global', 'admin']}>
                <Navigate to="/communications?tab=accounts" replace />
              </ProtectedRoute>
            } />
            <Route path="/settings/whatsapp" element={
              <ProtectedRoute allowedRoles={['management_functional', 'management_global', 'admin']}>
                <Navigate to="/communications?tab=accounts" replace />
              </ProtectedRoute>
            } />

            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />

            <Route path="/admin/functional-teams" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <FunctionalGroupsPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/integrations" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <IntegrationsPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/ai-model" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AIModelPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/backup" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <BackupPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/whatsapp-templates" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <WhatsAppTemplatesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/purpose-tags" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PurposeTagsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/ai-usage" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AIUsagePage />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/soa-settings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SOASettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/sso-settings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SSOSettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/org-adoption" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <OrgAdoptionPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/org-adoption/inactive" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <OrgAdoptionInactivePage mode="inactive" />
              </ProtectedRoute>
            } />

            <Route path="/admin/org-adoption/wau" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <OrgAdoptionInactivePage mode="wau" />
              </ProtectedRoute>
            } />

            <Route path="/admin/org-adoption/mau" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <OrgAdoptionInactivePage mode="mau" />
              </ProtectedRoute>
            } />

            {/* Public SSO launch — Nexus Hub configures this URL (no /api/ prefix). */}
            {/* This bridge forwards to the real backend /api/sso/nexus endpoint. */}
            <Route path="/sso/nexus" element={<SSONexusLaunch />} />

            {/* Public SSO callback — handles tokens minted by /api/sso/nexus */}
            <Route path="/sso/callback" element={<SSOCallback />} />
            
            <Route path="/studio/content" element={
              <ProtectedRoute>
                <ContentStudio />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/content-v2" element={
              <ProtectedRoute>
                <ContentStudioV2 />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/content-v2-test" element={
              <ProtectedRoute>
                <ContentStudioV2TestHarness />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/live" element={
              <ProtectedRoute>
                <LiveStudio />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/live-v2" element={
              <ProtectedRoute>
                <LiveStudioV2 />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/live-v2/teleprompter" element={
              <ProtectedRoute>
                <Teleprompter />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/live-monitor" element={
              <ProtectedRoute>
                <LiveMonitor />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/copy" element={
              <ProtectedRoute>
                <CopywritingStudio />
              </ProtectedRoute>
            } />
            
            <Route path="/studio/analyst" element={
              <ProtectedRoute allowedRoles={['executor', 'supervisor', 'management', 'admin']}>
                <AnalystStudio />
              </ProtectedRoute>
            } />
            
            <Route path="/soa-drill" element={
              <ProtectedRoute>
                <SOADrill />
              </ProtectedRoute>
            } />
            
            {/* Knowledge Pack Manager V3 (Default Route) */}
            <Route path="/knowledge-packs" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackManagerV3 />
              </ProtectedRoute>
            } />
            
            {/* Campaign Graph View */}
            <Route path="/campaigns/graph" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <CampaignGraphView />
              </ProtectedRoute>
            } />
            
            {/* Knowledge Pack Wizard V3 Compact Routes */}
            <Route path="/knowledge-packs/wizard" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackWizardCompact />
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge-packs/wizard/:packId" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackWizardCompact />
              </ProtectedRoute>
            } />
            
            {/* Product Pack V4 Editor Route */}
            <Route path="/product-packs/editor/:packId" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <ProductPackEditorV4 />
              </ProtectedRoute>
            } />
            
            {/* Ambassador Pack V4 Editor Routes */}
            <Route path="/ambassador-packs/v4/create" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <AmbassadorPackEditorV4 />
              </ProtectedRoute>
            } />
            
            <Route path="/ambassador-packs/v4/:packId" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <AmbassadorPackEditorV4 />
              </ProtectedRoute>
            } />
            
            {/* Legacy Multi-Step Wizard (Backup) */}
            <Route path="/knowledge-packs/wizard-old" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackWizardV3 />
              </ProtectedRoute>
            } />
            
            {/* Legacy V2 Routes (Backup) */}
            <Route path="/knowledge-packs-v2" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackManager />
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge-packs/wizard-v2" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackWizard />
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge-packs/wizard-v2/:packId" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackWizard />
              </ProtectedRoute>
            } />
            
            <Route path="/spider-web" element={
              <ProtectedRoute allowedRoles={['executor', 'supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <SpiderWeb />
              </ProtectedRoute>
            } />
            
            <Route path="/spider-web-v3" element={
              <ProtectedRoute allowedRoles={['executor', 'supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <SpiderWebV3 />
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge-packs/:packId/versions" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackVersionHistory />
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge-packs/:packId/audit" element={
              <ProtectedRoute allowedRoles={['supervisor', 'management', 'management_functional', 'management_global', 'admin']}>
                <KnowledgePackAuditLog />
              </ProtectedRoute>
            } />
            
            {/* Campaign Direction Manager - Strategic Direction Management */}
            <Route path="/campaign-direction" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <CampaignDirectionManager />
              </ProtectedRoute>
            } />
            
            {/* Campaign Routes (Phase 4A - Campaign Layer) */}
            <Route path="/campaigns" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <CampaignDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/campaigns/create" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <CampaignWizardCompact />
              </ProtectedRoute>
            } />
            
            <Route path="/campaigns/:id" element={
              <ProtectedRoute allowedRoles={['management', 'management_functional', 'management_global', 'admin']}>
                <CampaignWizardCompact />
              </ProtectedRoute>
            } />
            
            {/* Tutorial Page - Accessible by all authenticated users */}
            <Route path="/tutorial" element={
              <ProtectedRoute>
                <Tutorial />
              </ProtectedRoute>
            } />
            
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AppLayout>
          </NavigationProvider>
        </BrowserRouter>
      </LoadingProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
